import { useRef, useState } from "react";
import { Button, Empty, Spin, Typography } from "antd";
import { useFormatTemplateStore } from "@/state/formatTemplateStore";
import { bufferToBase64Image } from "@/utils/formatTemplate/slides-extraction-handlers";
import useFormatTemplates from "@/utils/formatTemplate/useFormatTemplates";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import useFormatTemplatesRecentlyUsed from "@/utils/formatTemplate/useRecentFormatTemplates";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import StarIcon from "@/formatTemplate/components/StarIcon";
const { Text, Title } = Typography;

export type FormatTemplateSearchProps = {
  isSelectionTemplate?: boolean;
  onTemplateSelect?: (template: any) => void;
  selectionMode?: boolean;
  selectedTemplateId?: string | null;
  hideStarButtons?: boolean;
  hidePrimaryLegend?: boolean;
  showSelectionLegend?: boolean;
  hideRing?: boolean;
  selectionBorderColor?: string;
  hideSelectionIntroText?: boolean;
  /** When true (e.g. beside Implied Colors panel), show 3 columns / slides per view instead of 4. */
  impliedColors?: boolean;
};

export function FormatTemplateSearch({
  isSelectionTemplate = false,
  onTemplateSelect,
  selectionMode = false,
  selectedTemplateId: controlledSelectedId,
  hideStarButtons = false,
  hidePrimaryLegend = false,
  showSelectionLegend = false,
  hideRing: _hideRing = false,
  selectionBorderColor,
  hideSelectionIntroText = false,
  impliedColors = false,
}: FormatTemplateSearchProps = {}) {
  void _hideRing;
  const slidesPerView = impliedColors ? 2 : 4;
  const { setFormatTemplateSidebar } = useFormatTemplateStore();
  const listRef = useRef<HTMLDivElement>(null);
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<any>(null);

  const isControlledSelection = controlledSelectedId !== undefined;
  const selectedId: string | null = isControlledSelection
    ? (controlledSelectedId as string | null)
    : internalSelectedId;

  const effectiveSelection = Boolean(isSelectionTemplate || selectionMode);
  const showRecentlyUsed = !isSelectionTemplate || !!selectionMode;

  const {
    formatTemplates,
    searchQuery,
    isLoading,
    hasMore,
    loadMore,
    handleSearchChange,
    fetchTemplates,
    handlePrimaryTemplate,
  } = useFormatTemplates(10);
  const {
    formatTemplatesRecentlyUsed,
    isLoading: isLoadingRecentlyUsed,
    fetchTemplates: fetchTemplatesRecentlyUsed,
  } = useFormatTemplatesRecentlyUsed(10);

  const limitedRecentlyUsedTemplates = formatTemplatesRecentlyUsed?.slice(0, 10) || [];

  const handleSelectFormat = (format: any) => {
    if (!isControlledSelection) {
      setInternalSelectedId(format.id);
    }
    onTemplateSelect?.(format);
  };

  const handleScroll = () => {
    if (!listRef.current || isLoading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom) {
      loadMore();
    }
  };

  const getImageSrc = (img: any) => bufferToBase64Image(img?.buffer?.data);

  const selectedBorderStyle = (formatId: string) =>
    effectiveSelection && selectedId === formatId
      ? {
          borderColor: selectionBorderColor || "#3b82f6",
          borderWidth: "2px",
        }
      : undefined;

  const selectedBorderClass = (formatId: string) =>
    effectiveSelection && selectedId === formatId ? "" : "border-black";

  const content = (
    <div
      className={`flex flex-col h-full min-h-0 grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 ${
        selectionMode ? "" : isSelectionTemplate ? "" : "min-h-screen"
      }`}
      style={{ overflow: "visible" }}
    >
      {isSelectionTemplate && !hideSelectionIntroText ? (
        <Text className="text-gray-600 leading-relaxed mb-6">
          Choose a format template to use for your chart. Use the star icon to make a template primary. The primary
          template will be used for your Chart Styling .
        </Text>
      ) : !isSelectionTemplate ? (
        <>
          <Text>
            Any content created on the AlphaScript platform is designed in the Format Template of your choosing. Below
            you can select your primary template, review the current list of available templates within your
            Organization and Create New Format Templates.
          </Text>
          <div className={"w-1/2 flex gap-10 my-6 items-center"}>
            <Text>Create New Template:</Text>
            <Button type={"primary"} onClick={() => setFormatTemplateSidebar("create-new")}>
              Create New
            </Button>
          </div>
        </>
      ) : null}

      {showRecentlyUsed && (
        <div className="flex flex-col mt-0" style={{ overflow: "visible" }}>
          <Title level={3} className={"font-bold"}>
            Recently Used
          </Title>
          {isLoadingRecentlyUsed ? (
            <div className="w-full py-6 flex items-center justify-center">
              <Spin size="large" />
            </div>
          ) : limitedRecentlyUsedTemplates && limitedRecentlyUsedTemplates.length > 0 ? (
            <div
              className="w-full max-w-[95%] mx-auto relative"
              style={{ overflow: "visible", paddingLeft: "20px", paddingRight: "20px" }}
            >
              {limitedRecentlyUsedTemplates.length > slidesPerView && (
                <>
                  <button
                    ref={prevButtonRef}
                    className="absolute -left-3 z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-35 disabled:cursor-not-allowed bg-transparent border-none p-0 cursor-pointer"
                    aria-label="Previous slide"
                    style={{
                      pointerEvents: "auto",
                      top: "calc((180px * 111 / 205) / 2 - 50px)",
                    }}
                  >
                    <svg width="25" height="120" viewBox="0 0 50 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="40,5 40,95 10,50" fill="#D3D3D3" stroke="#333" strokeWidth="1" />
                    </svg>
                  </button>
                  <button
                    ref={nextButtonRef}
                    className="absolute -right-3 z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-35 disabled:cursor-not-allowed bg-transparent border-none p-0 cursor-pointer"
                    aria-label="Next slide"
                    style={{
                      pointerEvents: "auto",
                      top: "calc((180px * 111 / 205) / 2 - 50px)",
                    }}
                  >
                    <svg width="25" height="120" viewBox="0 0 50 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="10,5 10,95 40,50" fill="#D3D3D3" stroke="#333" strokeWidth="1" />
                    </svg>
                  </button>
                </>
              )}
              <Swiper
                modules={[Navigation]}
                spaceBetween={0}
                slidesPerView={slidesPerView}
                navigation={
                  limitedRecentlyUsedTemplates.length > slidesPerView
                    ? {
                        prevEl: prevButtonRef.current,
                        nextEl: nextButtonRef.current,
                      }
                    : false
                }
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                onBeforeInit={(swiper) => {
                  if (swiper.params.navigation && typeof swiper.params.navigation !== "boolean") {
                    const navigation = swiper.params.navigation as any;
                    navigation.prevEl = prevButtonRef.current;
                    navigation.nextEl = nextButtonRef.current;
                  }
                }}
                className={"w-full overflow-visible"}
              >
                {limitedRecentlyUsedTemplates?.map((format, index) => (
                  <SwiperSlide key={`recent-${index}`}>
                    <div className="flex flex-col items-center w-full mx-auto max-w-[180px]">
                      <div
                        className={`relative aspect-[205/111] w-full h-26 border flex items-center justify-center text-white group ${
                          effectiveSelection ? "cursor-pointer transition-all" : ""
                        } ${selectedBorderClass(format.id)}`}
                        style={selectedBorderStyle(format.id)}
                        onClick={effectiveSelection ? () => handleSelectFormat(format) : undefined}
                      >
                        {!hideStarButtons && (
                          <button
                            type="button"
                            className={`absolute top-1 right-1 flex items-center ${
                              format.isPrimary ? "opacity-100" : "opacity-0 group-hover:opacity-100 cursor-pointer"
                            } transition-opacity z-10 text-[#FF5C35]`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrimaryTemplate(
                                {
                                  isPrimary: format.isPrimary,
                                  formatId: format.id,
                                  orgId: format.orgId,
                                },
                                fetchTemplates,
                                fetchTemplatesRecentlyUsed
                              );
                            }}
                          >
                            <StarIcon isFill={format.isPrimary} />
                          </button>
                        )}
                        {format.frontCoverImage ? (
                          <img
                            src={getImageSrc(format.frontCoverImage)}
                            className="w-full h-full object-cover"
                            draggable="false"
                            alt={`${format.formatName.replace("-", " - ")} Front Cover`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">{index}</div>
                        )}
                      </div>
                      <h3 className="text-sm font-medium text-gray-700 text-center truncate w-full">
                        {format.formatName.replace("-", " - ")}
                      </h3>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="w-full h-26 flex justify-center">
              <Empty description="No format templates available." />
            </div>
          )}
        </div>
      )}

      {!hidePrimaryLegend && (
        <div className="flex items-center mt-4 space-x-2 text-[#FF5C35]">
          <StarIcon isFill={true} />
          <span className="text-sm font-medium text-gray-700">Primary Template</span>
        </div>
      )}

      {showSelectionLegend && (
        <div className="flex items-center mt-4 space-x-2">
          <div className="w-8 h-4 border-2" style={{ borderColor: selectionBorderColor || "#3b82f6" }} />
          <span className="text-sm font-medium text-gray-700">Selected for the Analysis</span>
        </div>
      )}

      <div className="flex flex-col mt-6">
        <Title level={3} className={"font-bold"}>
          Available in Your Organization
        </Title>
        <input
          className="w-1/2 border my-2 px-2 py-0.5 rounded border-black"
          type="text"
          placeholder="Search By Company Name"
          value={searchQuery}
          onChange={handleSearchChange}
        />

        {formatTemplates && formatTemplates.length > 0 ? (
          <div
            ref={listRef}
            onScroll={() => {
              handleScroll();
            }}
            className={`mt-3 overflow-y-auto h-[270px] grid gap-y-3 ${impliedColors ? "grid-cols-2" : "grid-cols-4"}`}
          >
            {formatTemplates?.map((format, num) => (
              <div key={num} className="flex flex-col items-center gap-1 w-fit">
                <div
                  className={`relative aspect-[205/111] w-45 h-26 border flex items-center justify-center text-white group transition-all duration-200 ${
                    effectiveSelection ? "cursor-pointer" : ""
                  } ${selectedBorderClass(format.id)} ${effectiveSelection && selectedId !== format.id ? "hover:border-[#FF5C35]" : ""}`}
                  style={selectedBorderStyle(format.id)}
                  onClick={effectiveSelection ? () => handleSelectFormat(format) : undefined}
                >
                  {!isSelectionTemplate && (
                    <>
                      <button
                        type="button"
                        className={`absolute top-1 right-1 flex items-center ${
                          format.isPrimary ? "opacity-100" : "opacity-0 group-hover:opacity-100 cursor-pointer"
                        } transition-opacity z-10 text-[#FF5C35]`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrimaryTemplate(
                            {
                              isPrimary: format.isPrimary,
                              formatId: format.id,
                              orgId: format.orgId,
                            },
                            fetchTemplates,
                            fetchTemplatesRecentlyUsed
                          );
                        }}
                      >
                        <StarIcon isFill={format.isPrimary} />
                      </button>
                      <button
                        className="absolute bottom-1 flex items-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity z-10 text-[#FF5C35]
                        !bg-gray-800 !text-white hover:!bg-gray-700 px-4 py-1 rounded-md font-medium text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const { setFormatId, setFormatTemplateSidebar } = useFormatTemplateStore.getState();
                          setFormatTemplateSidebar("update");
                          setFormatId(format.id);
                        }}
                      >
                        View & Edit Template
                      </button>
                    </>
                  )}
                  {isSelectionTemplate && selectionMode && !hideStarButtons && (
                    <button
                      type="button"
                      className={`absolute top-1 right-1 flex items-center ${
                        format.isPrimary ? "opacity-100" : "opacity-0 group-hover:opacity-100 cursor-pointer"
                      } transition-opacity z-10 text-[#FF5C35]`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrimaryTemplate(
                          {
                            isPrimary: format.isPrimary,
                            formatId: format.id,
                            orgId: format.orgId,
                          },
                          fetchTemplates,
                          fetchTemplatesRecentlyUsed
                        );
                      }}
                    >
                      <StarIcon isFill={format.isPrimary} />
                    </button>
                  )}
                  {format.frontCoverImage ? (
                    <img
                      src={getImageSrc(format.frontCoverImage)}
                      className="w-full h-full object-contain"
                      draggable="false"
                      alt={`${format.formatName.replace("-", " - ")} Front Cover`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">{num}</div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-gray-700"> {format.formatName.replace("-", " - ")}</h3>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-[270px] flex justify-center py-10">
            <Empty description="No format templates available." />
          </div>
        )}
      </div>
    </div>
  );

  return content;
}
